import React from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';

import { AppLink, routeNaming } from '../../routes';
import { enforceParameters } from '../../hocs/enforce-parameters';
import { ProductImage } from '../product-image';
import { IntranetLink } from '../intranet-link';
import { CompareCheckbox } from '../compare-checkbox/CompareCheckbox';
import styles from './IntranetProductCard.css';

const IPropTypes = {
  className: PropTypes.string,
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    ableToSell: PropTypes.bool.isRequired,
    shouldShowPrice: PropTypes.func.isRequired,
    brand: PropTypes.string.isRequired,
    characteristics: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string.isRequired,
    })).isRequired,
    financing: PropTypes.shape({
      priceString: PropTypes.string,
      zeroDayExpiry: PropTypes.arrayOf(PropTypes.shape({
        amount: PropTypes.number.isRequired,
        priceString: PropTypes.string.isRequired,
      })).isRequired,
    }).isRequired,
    coverImage: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    stockIsAvailable: PropTypes.func,
    stockIsAlmostNone: PropTypes.func,
    stockIsEmpty: PropTypes.func,
    stockIsNearlyEmpty: PropTypes.func,
    inUse: PropTypes.bool,
    inWeb: PropTypes.bool,
    specificId: PropTypes.number,
  }).isRequired,
};

const defaultProps = {
  className: '',
};

const renderImage = (id, coverImage, showNormalLink, specificId) => {
  const productImage = (
    <ProductImage
      className={styles.image}
      src={coverImage}
      size="sm"
    />
  );
  if (!showNormalLink) {
    return (
      <IntranetLink
        className={styles.image}
        productId={id}
        specificId={specificId}
      >
        {productImage}
      </IntranetLink>
    );
  }
  return (
    <AppLink
      routeName={routeNaming.PRODUCT_DETAIL}
      pathParams={{ id }}
    >
      {productImage}
    </AppLink>
  );
};

const renderTitle = (id, name, showNormalLink, specificId) => {
  const title = (
    <h2 className={styles.name}>
      {name}
    </h2>
  );
  if (!showNormalLink) {
    return (
      <IntranetLink
        className={styles.imageLink}
        productId={id}
        specificId={specificId}
      >
        {title}
      </IntranetLink>
    );
  }
  return (
    <AppLink
      routeName={routeNaming.PRODUCT_DETAIL}
      pathParams={{ id }}
      className={styles.imageLink}
    >
      {title}
    </AppLink>
  );
};

const renderStock = (product) => {
  if (product.isService) {
    return null;
  }
  let stockLevel = styles.availableStockLevel;
  if (!product.inUse) {
    stockLevel = styles.notInUseStockLevel;
  } else if (product.stockIsEmpty()) {
    stockLevel = styles.emptyStockLevel;
  } else if (product.stockIsAlmostNone()) {
    stockLevel = styles.almostEmptyStockLevel;
  } else if (product.stockIsNearlyEmpty()) {
    stockLevel = styles.nearlyEmptyStockLevel;
  }
  return <div className={[styles.stock, stockLevel].join(' ')} />;
};

const renderCharacteristics = characteristics => (
  characteristics
    .slice(0, 2)
    .map(characteristic => characteristic.text)
    .join(', ')
);

const renderContentIntranet = (className, product, financingWithMaxAmount) => {
  const showNormalLink = !!product.characteristics.length && !product.specificId;
  return (
    <React.Fragment>
      <article data-tip data-for={`product-${product.id}`} className={[className, styles.container].join(' ')}>
        {
          product.notAbleToSellString &&
          <ReactTooltip id={`product-${product.id}`} type="error">
            <span>
              {product.notAbleToSellString}
            </span>
          </ReactTooltip>
        }
        {
          renderStock(product)
        }
        {
          renderTitle(
            product.id,
            product.name,
            showNormalLink,
            product.specificId,
          )
        }
        <div className={styles.imageContainer}>
          {
            renderImage(
              product.id,
              product.coverImage,
              showNormalLink,
              product.specificId,
            )
          }
          <div>
            <p className={styles.brand}>{product.brand}</p>
            {
              product.shouldShowPrice() &&
              <div>
                <p className={styles.price}>${product.financing.priceString}</p>
                {
                  financingWithMaxAmount &&
                  <p className={styles.intranetFinancing}>
                    ${financingWithMaxAmount.priceString} x {financingWithMaxAmount.amount} cuotas
                  </p>
                }
              </div>
            }
            {
              !product.shouldShowPrice() &&
              <p className={styles.financing}>Precio sin determinar</p>
            }
          </div>
        </div>
        <div>
          <p className={styles.characteristics}>
            {renderCharacteristics(product.characteristics)}
          </p>
          <CompareCheckbox product={product} />
        </div>
      </article>
    </React.Fragment>
  );
};

const IntranetProductCard = (props) => {
  const { product: { financing: { zeroDayExpiry } } } = props;
  const financingWithMaxAmount = !!zeroDayExpiry.length && zeroDayExpiry.reduce((a, b) => (
    (a.amount > b.amount)
      ? a
      : b
  ));
  return renderContentIntranet(props.className, props.product, financingWithMaxAmount);
};

IntranetProductCard.propTypes = IPropTypes;
IntranetProductCard.defaultProps = defaultProps;

const WrappedIntranetProductCard = enforceParameters(IntranetProductCard);

export { WrappedIntranetProductCard as IntranetProductCard };



// WEBPACK FOOTER //
// ./src/common/intranet-product-card/IntranetProductCard.jsx